import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ExternalLink, ShieldCheck, Search, Sparkles } from "lucide-react";
import { FOCUS_AREAS } from "@/lib/focusAreas";
import { useCoachReadiness } from "@/hooks/useCoachReadiness";
import { useIsPro } from "@/hooks/useSubscription";
import ProLockPill from "@/components/payments/ProLockPill";

type Program = {
  id: string;
  title: string;
  program_url: string | null;
  focus_areas: string[];
  award_min: number | null;
  award_max: number | null;
  cycle: string | null;
  next_deadline: string | null;
  eligibility: string | null;
  description: string | null;
  opportunity_type: string | null;
  provider_agency: string | null;
  funders: { name: string; slug: string; funder_type: string } | null;
};

const OPPORTUNITY_TYPES: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "grant", label: "Grants" },
  { key: "loan", label: "Loans" },
  { key: "microloan", label: "Microloans" },
  { key: "revolving_loan_fund", label: "RLFs" },
  { key: "mentorship", label: "Mentorship" },
  { key: "technical_assistance", label: "Technical assistance" },
  { key: "contract", label: "Contracts" },
  { key: "tax_credit", label: "Tax credits" },
];

const OPPORTUNITY_TYPE_LABEL: Record<string, string> = {
  grant: "Grant",
  loan: "Loan",
  microloan: "Microloan",
  revolving_loan_fund: "Revolving loan fund",
  mentorship: "Mentorship",
  technical_assistance: "Technical assistance",
  contract: "Contract",
  incubator: "Incubator",
  tax_credit: "Tax credit",
  equity_investment: "Equity",
};

const ProgramsDirectory = () => {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState<string | null>(null);
  const [cycle, setCycle] = useState<string | null>(null);
  const [oppType, setOppType] = useState<string>("all");
  const { hasAssessment, focusAreas: coachFocus } = useCoachReadiness();
  const [coachOnly, setCoachOnly] = useState(false);
  const isPro = useIsPro();

  const isCoachMatch = (p: Program) =>
    coachFocus.length > 0 && (p.focus_areas ?? []).some((f) => coachFocus.includes(f as any));

  const { data: programs, isLoading } = useQuery({
    queryKey: ["grant-programs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("grant_programs")
        .select("id, title, program_url, focus_areas, award_min, award_max, cycle, next_deadline, eligibility, description, opportunity_type, provider_agency, funders:funder_id(name, slug, funder_type)")
        .eq("verified", true)
        .order("title");
      if (error) throw error;
      return (data ?? []) as unknown as Program[];
    },
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (programs ?? []).filter((p) => {
      if (oppType !== "all" && (p.opportunity_type ?? "grant") !== oppType) return false;
      if (focus && !p.focus_areas?.includes(focus)) return false;
      if (cycle && p.cycle !== cycle) return false;
      if (coachOnly && !isCoachMatch(p)) return false;
      if (!needle) return true;
      return (
        p.title.toLowerCase().includes(needle) ||
        p.description?.toLowerCase().includes(needle) ||
        p.funders?.name?.toLowerCase().includes(needle) ||
        p.provider_agency?.toLowerCase().includes(needle)
      );
    });
  }, [programs, q, focus, cycle, coachOnly, coachFocus, oppType]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPPORTUNITY_TYPES.map((t) => (
          <Button
            key={t.key}
            size="sm"
            variant={oppType === t.key ? "default" : "outline"}
            onClick={() => setOppType(t.key)}
            className="text-xs"
          >
            {t.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search programs, funders…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        {hasAssessment && coachFocus.length > 0 && (
          <Button
            size="sm"
            variant={coachOnly ? "default" : "outline"}
            onClick={() => setCoachOnly((v) => !v)}
            className="gap-1"
          >
            <Sparkles className="h-3 w-3" /> Coach recommended
          </Button>
        )}
        {isPro ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={focus === null ? "default" : "outline"} onClick={() => setFocus(null)}>All focus</Button>
              {FOCUS_AREAS.map((f) => (
                <Button key={f} size="sm" variant={focus === f ? "default" : "outline"} onClick={() => setFocus(f)}>
                  {f}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              {["Rolling", "Annual", "One-time"].map((c) => (
                <Button key={c} size="sm" variant={cycle === c ? "default" : "outline"} onClick={() => setCycle(cycle === c ? null : c)}>
                  {c}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <ProLockPill label="Advanced filters" />
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading programs…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <p className="text-sm text-muted-foreground">No programs match those filters.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/coach">Ask Coach to find matches for your business</Link>
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="border border-border rounded-lg p-4 bg-card hover:border-primary/40 transition">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="font-semibold text-sm leading-tight">{p.title}</h4>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-800 border-emerald-500/40">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </Badge>
                  {hasAssessment && isCoachMatch(p) && (
                    <Badge variant="outline" className="gap-1 text-[10px] bg-primary/10 text-primary border-primary/30">
                      <Sparkles className="h-3 w-3" /> Coach match
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {p.opportunity_type && p.opportunity_type !== "grant" && (
                  <Badge variant="secondary" className="text-[10px] capitalize">
                    {OPPORTUNITY_TYPE_LABEL[p.opportunity_type] ?? p.opportunity_type.replace(/_/g, " ")}
                  </Badge>
                )}
                {p.provider_agency && (
                  <Badge variant="outline" className="text-[10px]">
                    {p.provider_agency.replace(/_/g, " ")}
                  </Badge>
                )}
              </div>
              {p.funders && (
                <p className="text-xs text-muted-foreground mb-2">
                  <Link to={`/funders/${p.funders.slug}`} className="hover:text-primary hover:underline">
                    {p.funders.name}
                  </Link>{" "}
                  · {p.funders.funder_type}
                </p>
              )}
              {p.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-3">{p.description}</p>}
              <div className="flex flex-wrap gap-2 text-[10px] mb-3">
                {p.cycle && <Badge variant="outline">{p.cycle}</Badge>}
                {(p.award_min || p.award_max) && (
                  <Badge variant="outline">
                    ${(p.award_min ?? 0).toLocaleString()} – ${(p.award_max ?? p.award_min ?? 0).toLocaleString()}
                  </Badge>
                )}
                {p.focus_areas?.slice(0, 2).map((f) => (
                  <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>
                ))}
              </div>
              {p.program_url && (
                <a href={p.program_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                  View program <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramsDirectory;